import {
  WppDivider,
  WppTypography,
  WppInput,
  WppListItem,
  WppSelect,
  WppButton,
  WppPillGroup,
  WppPill,
  WppAutocomplete,
} from '@wppopen/components-library-react'
import { AutocompleteDefaultOption, InputChangeEventDetail, SelectChangeEventDetail } from '@wppopen/components-library'
import { WppInputCustomEvent } from '@wppopen/components-library/dist/types/components'
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import regionFormReducer, {
  emptyRegionFormReducerState,
  handleError,
  updateInput,
  RegionFormReducerState,
  UpdateInputField,
  UpdateSelectField,
  SelectField,
  UpdateAutoCompleteField,
  updateAutoComplete,
  setSelect,
  UpdateRegionListField,
  resetForm,
} from 'reducers/CreateAndEditRegionReducer/CreateAndEditRegionReducer'
import FormSkeleton from 'components/Skeletons/FormSkeleton'
import { supportedLanguages, transformRegionData } from './utils'
import { useNavigate } from 'react-router-dom'
import { getType } from 'utils'
import { CircleFlag, countries } from 'react-circle-flags'
import SelectedValues from 'components/SelectedValues/SelectedValues'
import { createRoot } from 'react-dom/client'
import { GenericModal } from 'components/Modals'
import appContext from 'context/appContext'
import usePermissions from 'hooks/usePermissions'
import FormList from 'components/FormComponents/FormList/FormList'
import useInitialiser from 'hooks/useInitialiser'
import { OnSubmitGenericModalProps } from 'components/Modals/GenericModal/helper'
import useApi from 'hooks/useApi'
import useCurrentUser from 'hooks/useCurrentUser'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'
import { BANNER_KEYS, ERROR_MESSAGES } from 'utils/constants'
import type { ArchiveCreateInput, ArchiveCreateOutput, ConfigGETResponseSuccess, RegionsDetailData } from '@contracts'
import type { RegionsCreatePayloadV5 } from 'utils/utils.d'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils.d'
import formStyles from '../SettingsForm.module.scss'

interface CreateAndEditRegionFormProps {
  initialData?: RegionsDetailData | null
  onSubmitForm: (data: RegionsCreatePayloadV5) => Promise<string>
  onCancelForm: () => void
  isApiLoading?: boolean
  isFormActive?: boolean
  configRetailers: ConfigGETResponseSuccess['retailers']
  errors?: Error | null
  setErrorToastDetails?: Dispatch<SetStateAction<ToastDetails | undefined>>
}

const { MANDATORY_FIELD } = ERROR_MESSAGES
const { SETTINGS_HOME } = BANNER_KEYS

const CreateAndEditRegionForm = ({
  initialData,
  onSubmitForm,
  onCancelForm,
  isApiLoading,
  isFormActive = true,
  configRetailers,
  errors,
  setErrorToastDetails,
}: CreateAndEditRegionFormProps) => {
  const navigate = useNavigate()
  const { userName } = useCurrentUser()
  const { setActiveBannerId, setToastDetailsContext } = useContext(SettingsPageContext)
  const { configApi } = useContext(appContext)
  const { fetchData: fetchConfigApi, url: configApiURL, isLoading: isConfigLoading } = configApi
  const initialRegionFormReducerState = useMemo<RegionFormReducerState>(() => {
    if (initialData && configRetailers) return transformRegionData(initialData, configRetailers)
    return emptyRegionFormReducerState
  }, [configRetailers, initialData])
  const [formReducerState, formReducerDispatch] = useReducer(regionFormReducer, initialRegionFormReducerState)
  const [isCreateAndEditModalOpen, setIsCreateAndEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)

  const { clientsWithPermissions } = usePermissions({
    permissionSubset: ['PDP_APP_POST_REGIONS_BY_ARCHIVE'],
  })

  const typedFormState = formReducerState as RegionFormReducerState

  const {
    fetchData: postArchiveRegion,
    isLoading: isPostArchiveRegionLoading,
    url: postRegionRegionUrl,
  } = useApi<ArchiveCreateOutput>(initialData ? `regions/${initialData._id}/archive` : '')

  const [clientsWithPostArchiveRegion] = clientsWithPermissions

  const { name, locale, flag, guidelines, retailers } = typedFormState
  const requiredFields = {
    name,
    locale,
    flag,
    retailers,
  } as Partial<RegionFormReducerState>

  const flagSelectRef = useRef<HTMLWppSelectElement | null>(null)

  const handleInput = (e: WppInputCustomEvent<InputChangeEventDetail>) => {
    const { value, name } = e.target

    // ensure that the name is a valid input field according to what is defined in the reducer
    const typeSafeInputFieldKey = name as UpdateInputField

    formReducerDispatch(updateInput(typeSafeInputFieldKey, value))

    // handle mandatory field error message
    if (
      Object.keys(requiredFields).includes(typeSafeInputFieldKey) &&
      Reflect.has(typedFormState[typeSafeInputFieldKey], 'error')
    ) {
      // if the field is not empty and there is an error message, remove the error message
      if (!typedFormState[typeSafeInputFieldKey].error && !!value.trim()) return
      formReducerDispatch(handleError(typeSafeInputFieldKey, !!value.trim() ? undefined : MANDATORY_FIELD))
    }
  }

  // handle select field changes
  const handleSelectEl = (e: CustomEvent<SelectChangeEventDetail>, fieldName: SelectField) => {
    e.preventDefault()
    const { value } = e.target as HTMLWppSelectElement

    formReducerDispatch(setSelect(fieldName as SelectField, value as string | string[]))

    // handle mandatory field error message
    if (Object.keys(requiredFields).includes(fieldName) && Reflect.has(typedFormState[fieldName], 'error')) {
      // if the field is not empty and there is an error message, remove the error message
      // single select (string) and multi select (string[]) can both be checked for length
      if (!typedFormState[fieldName].error && !!value.length) return
      formReducerDispatch(handleError(fieldName, !!value.length ? undefined : MANDATORY_FIELD))
    }
  }

  // handle autocomplete field changes
  const handleAutoCompleteChange = (value: AutocompleteDefaultOption[], fieldName: UpdateAutoCompleteField) => {
    formReducerDispatch(updateAutoComplete(fieldName, value))
    formReducerDispatch(handleError(fieldName, value.length ? undefined : MANDATORY_FIELD))
  }

  // handle form submit / validate form
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    let hasError = false

    // check if any of the required fields are empty
    Object.entries(requiredFields).forEach(([fieldName, { value }]) => {
      if ((typeof value === 'string' && !value.trim()) || (value instanceof Array && !value.length)) {
        hasError = true
        return formReducerDispatch(handleError(fieldName as keyof RegionFormReducerState, MANDATORY_FIELD))
      }
    })

    // if there are any errors in the form, do not proceed
    if (
      (initialData && JSON.stringify(initialRegionFormReducerState) === JSON.stringify(formReducerState)) ||
      hasError ||
      Object.values(formReducerState).some(field => Reflect.has(field, 'error') && !!field.error)
    )
      return
    setIsCreateAndEditModalOpen(true)
  }

  const handleModalSubmit = async () => {
    try {
      const regionPutPayload: RegionsCreatePayloadV5 = {
        name: name.value,
        locale: locale.value as RegionsCreatePayloadV5['locale'],
        flag: flag.value,
        config: {
          guidelines: guidelines.value,
        },
        retailers: retailers.value.map(({ id }) => id) as string[],
        update_info: {
          user_name: userName,
        },
      }

      const res = await onSubmitForm(regionPutPayload)
      await fetchConfigApi(configApiURL)
      if (!initialData) navigate(`/settings/region/${res}`)
    } catch (error) {
      console.error('🚀 ~ error:', error)
    } finally {
      setIsCreateAndEditModalOpen(false)
    }
  }

  const handleDeleteModalSubmit = async ({ comment }: OnSubmitGenericModalProps) => {
    try {
      const body: ArchiveCreateInput = {
        update_info: {
          user_name: userName,
          comment,
        },
      }

      await postArchiveRegion(postRegionRegionUrl, {
        method: 'POST',
        body: JSON.stringify(body),
      })

      setActiveBannerId(SETTINGS_HOME)
      setToastDetailsContext({
        type: 'success',
        message: 'Region successfully deleted',
      })
      navigate('/settings/regions')
    } catch (error) {
      if (setErrorToastDetails)
        setErrorToastDetails({
          type: 'error',
          message: 'Error: Unable to delete region',
        })
    } finally {
      setIsDeleteModalOpen(false)
    }
  }

  useEffect(() => {
    if (!errors || !isFormActive) return
    if (Array.isArray(errors)) {
      errors.forEach(error => {
        formReducerDispatch(handleError(error.path as keyof RegionFormReducerState, error.message))
      })
    }
  }, [errors, isFormActive])

  const flagListItems = useMemo(() => {
    return Object.keys(countries).map(country => {
      // handle bug in react-circle-flags where some flags are not supported
      const brokenRegionFlagCodes: (keyof typeof countries)[] = [
        'AFAR',
        'EARTH',
        'KURDISTAN',
        'MANIPUR',
        'MIZORAM',
        'NATO',
        'OLYMPICS',
        'TORRES_STRAIT_ISLANDS',
        'UNITED_NATIONS',
        'XX',
      ]

      // handle bug in react-circle-flags where 'filenames_without_extensions' and 'xx' are not supported
      if (brokenRegionFlagCodes.includes(country.toUpperCase())) return null
      return (
        <WppListItem value={country} key={country} data-testid="flags-select-item">
          <span slot="body">{country}</span>
          <span slot="label" data-testid="flag-li-span">
            <CircleFlag countryCode={country} height={24} />
          </span>
        </WppListItem>
      )
    })
  }, [])

  const retailersListItemElements = useMemo(() => {
    if (!configRetailers) return null
    const options: AutocompleteDefaultOption[] = configRetailers.map(retailer => ({
      id: retailer._id!,
      label: retailer.name,
    }))
    return options.map(option => (
      <WppListItem value={option} label={option.label} key={option.id} data-testid="retailer-option">
        <p slot="label" data-testid="retailer-option-text">
          {option.label}
        </p>
      </WppListItem>
    ))
  }, [configRetailers, typedFormState?.retailers])

  // Workaround to replace the select input text with the selected flag icon
  useEffect(() => {
    const flagSelect = flagSelectRef.current
    const shadowRoot = flagSelect?.shadowRoot
    const menuListElement = shadowRoot?.querySelector('.wpp-menu-list')
    const menuListElementShadowRoot = menuListElement?.shadowRoot
    if (!flagSelectRef.current || !typedFormState.flag.value || !shadowRoot || !menuListElementShadowRoot) return

    const elementToReplace = shadowRoot.querySelector('.input-text') || shadowRoot.querySelector('.input-flag')

    // Remove the element containing the text and insert the flag icon
    if (elementToReplace) {
      const flagIconHeight = 22
      const newElement = <CircleFlag countryCode={typedFormState.flag.value} height={flagIconHeight} />
      const newElementNode = document.createElement('div')
      newElementNode.className = 'input-flag'
      newElementNode.style.height = `${flagIconHeight}px`
      const root = createRoot(newElementNode)
      root.render(newElement)
      elementToReplace.replaceWith(newElementNode)
    }
  }, [JSON.stringify(typedFormState.flag.value)])

  // reset form if form is not active
  useEffect(() => {
    if (!isFormActive && !!initialData) formReducerDispatch(resetForm(initialRegionFormReducerState))
  }, [isFormActive])

  const { isInitialising } = useInitialiser({ subscribedApis: [configApi] })

  if (isInitialising) return <FormSkeleton />

  return (
    <div className={formStyles.form_wrapper} data-testid="form-wrapper">
      <WppDivider className={formStyles.divider} />
      <form onSubmit={handleSubmit} data-testid="region-form">
        <section>
          <WppTypography type="xl-heading" tag="h3">
            Region details
          </WppTypography>

          <WppInput
            name={'name' as UpdateInputField}
            placeholder="Enter region name e.g. EMEA"
            labelConfig={{ text: 'Region name' }}
            required
            disabled={!isFormActive}
            value={name.value}
            onWppChange={handleInput}
            messageType={!name.error ? undefined : 'error'}
            message={name.error}
            data-testid="region-name-input"
          />

          <WppSelect
            name={'locale' as UpdateSelectField}
            labelConfig={{ text: 'Language' }}
            required
            disabled={!!initialData}
            value={typedFormState.locale.value}
            maxItemsToDisplay={4}
            data-testid="language-select"
            placeholder="Please select"
            onWppChange={e => handleSelectEl(e, 'locale')}
            messageType={typedFormState.locale.error ? 'error' : undefined}
            message={typedFormState.locale.error}
          >
            {Array.from(supportedLanguages.keys())
              .filter(language => !initialData || language === initialData.locale)
              .map(language => (
                <WppListItem
                  key={language}
                  value={language}
                  selectable
                  data-testid="language-select-item"
                  multiple={false}
                >
                  <span slot="label" data-testid="language-li-span">
                    {language.toUpperCase().replace('_', '-')}
                  </span>
                </WppListItem>
              ))}
          </WppSelect>

          <WppSelect
            name={getType<UpdateSelectField>('flag')}
            labelConfig={{ text: 'Flag' }}
            required
            onWppChange={e => handleSelectEl(e, 'flag')}
            disabled={!isFormActive}
            data-testid="flags-select"
            placeholder="Please select"
            ref={flagSelectRef}
            messageType={typedFormState.flag.error ? 'error' : undefined}
            message={typedFormState.flag.error}
          >
            {flagListItems}
          </WppSelect>
        </section>

        <section>
          <WppTypography type="xl-heading" tag="h3">
            Retailers
          </WppTypography>

          <fieldset>
            <WppAutocomplete
              name={getType<UpdateAutoCompleteField>('retailers')}
              labelConfig={{
                text: 'Retailers',
              }}
              placeholder="Type to search"
              value={typedFormState.retailers.value}
              required
              onWppChange={e => handleAutoCompleteChange(e.detail.value as AutocompleteDefaultOption[], 'retailers')}
              data-testid="retailers-autocomplete"
              multiple
              simpleSearch
              disabled={!isFormActive}
              messageType={typedFormState.retailers.error ? 'error' : undefined}
              message={typedFormState.retailers.error}
            >
              {retailersListItemElements}
              <div slot="selected-values">
                <SelectedValues
                  values={typedFormState.retailers.value}
                  handleRemove={id =>
                    handleAutoCompleteChange(
                      typedFormState.retailers.value.filter(i => i.id !== id),
                      'retailers',
                    )
                  }
                />
              </div>
            </WppAutocomplete>

            {!!typedFormState?.retailers?.value.length && (
              <WppPillGroup type="display" data-testid="retailers-pills-container">
                {typedFormState?.retailers?.value.map(retailer => (
                  <WppPill
                    label={retailer.label}
                    value={retailer.label}
                    removable={isFormActive}
                    disabled={!isFormActive}
                    onWppClose={() =>
                      handleAutoCompleteChange(
                        typedFormState.retailers.value.filter(i => i.id !== retailer.id),
                        'retailers',
                      )
                    }
                    key={retailer.id}
                    data-testid="retailer-pill"
                  />
                ))}
              </WppPillGroup>
            )}
          </fieldset>
        </section>

        <FormList<'RegionFormReducerState'>
          id={getType<UpdateRegionListField>('guidelines')}
          label="Guidelines"
          disabled={!isFormActive}
          state={typedFormState}
          reducerDispatch={formReducerDispatch}
        />

        {isFormActive && (
          <>
            <WppDivider className={formStyles.divider} />
            <div className={formStyles.actions}>
              {!!clientsWithPostArchiveRegion?.length && initialData && (
                <WppButton
                  variant="destructive-secondary"
                  data-testid="delete-cta"
                  onClick={e => {
                    e.preventDefault()
                    if (!initialData) return
                    setIsDeleteModalOpen(true)
                  }}
                >
                  Delete Region
                </WppButton>
              )}

              <div>
                <WppButton
                  variant="secondary"
                  onClick={e => {
                    e.preventDefault()
                    onCancelForm()
                  }}
                  data-testid="cancel-cta"
                >
                  Cancel
                </WppButton>
                <WppButton type="submit" variant="primary" data-testid="save-changes-cta">
                  Save changes
                </WppButton>
              </div>
            </div>
          </>
        )}
      </form>
      {/* modal to save */}
      {isCreateAndEditModalOpen && (
        <GenericModal
          shouldModalOpen={isCreateAndEditModalOpen}
          modalTitle="Save changes"
          submitButtonText="Save"
          onCancel={() => setIsCreateAndEditModalOpen(false)}
          onSubmit={handleModalSubmit}
          isApiLoading={isApiLoading || isConfigLoading}
        />
      )}

      {/* modal to delete */}
      {!!clientsWithPostArchiveRegion?.length && initialData && isDeleteModalOpen && (
        <GenericModal
          shouldModalOpen={isDeleteModalOpen}
          modalTitle="Delete Region"
          submitButtonText="Delete"
          fieldsConfig={{
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
          onCancel={() => setIsDeleteModalOpen(false)}
          onSubmit={handleDeleteModalSubmit}
          isApiLoading={isPostArchiveRegionLoading}
        />
      )}
    </div>
  )
}

export default CreateAndEditRegionForm
